import React, { useMemo } from 'react';
import { Shield, AlertTriangle, AlertCircle, Info, CheckCircle2, BarChart3 } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { useQualityWorkspaceState } from './useQualityWorkspaceState';
export function GovernanceReport() {
  const {
    filteredRecords
  } = useQualityWorkspaceState();
  const rows = useMemo(() => filteredRecords.map(record => ({
    questionId: record.question.id,
    module: record.question.module,
    tier: record.question.tier,
    group: record.question.group,
    issues: record.issues.filter(i => i.category === 'governance')
  })).filter(item => item.issues.length > 0), [filteredRecords]);
  // Rule breakdown — which rules are violated most
  const ruleBreakdown = useMemo(() => {
    const ruleMap = new Map<string, {
      count: number;
      severity: string;
      message: string;
    }>();
    rows.forEach(row => {
      row.issues.forEach(issue => {
        // Extract rule ID from message if present (e.g. "MASTER_2_2")
        const ruleMatch = issue.message.match(/MASTER_\d+_\d+|ADDON_\w+_\d+/);
        const key = ruleMatch ? ruleMatch[0] : issue.message.slice(0, 60);
        const existing = ruleMap.get(key);
        if (existing) {
          existing.count++;
        } else {
          ruleMap.set(key, {
            count: 1,
            severity: issue.severity,
            message: issue.message
          });
        }
      });
    });
    return Array.from(ruleMap.entries()).map(([rule, data]) => ({
      rule,
      ...data
    })).sort((a, b) => b.count - a.count).slice(0, 10);
  }, [rows]);
  const criticalCount = rows.reduce((sum, r) => sum + r.issues.filter(i => i.severity === 'critical').length, 0);
  const warningCount = rows.reduce((sum, r) => sum + r.issues.filter(i => i.severity === 'warning').length, 0);
  return <div className="space-y-4" data-id="element-2381">
      <Card className="p-5" data-id="element-2382">
        <div className="flex items-center justify-between mb-4" data-id="element-2383">
          <div className="flex items-center gap-2" data-id="element-2384">
            <div className="p-2 bg-purple-50 rounded-lg" data-id="element-2385">
              <Shield className="w-5 h-5 text-purple-600" data-id="element-2386" />
            </div>
            <div data-id="element-2387">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-2388">
                Governance Report
              </h3>
              <p className="text-sm text-slate-500" data-id="element-2389">
                {rows.length} questions with governance issues
              </p>
            </div>
          </div>
          <div className="flex gap-2" data-id="element-2390">
            {criticalCount > 0 && <Badge variant="danger" size="sm" data-id="element-2391">
                {criticalCount} critical
              </Badge>}
            {warningCount > 0 && <Badge variant="warning" size="sm" data-id="element-2392">
                {warningCount} warnings
              </Badge>}
            {rows.length === 0 && <Badge variant="success" size="sm" data-id="element-2393">
                All passing
              </Badge>}
          </div>
        </div>

        {/* Rule breakdown summary */}
        {ruleBreakdown.length > 0 && <div data-id="element-2394">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-2395">
              <BarChart3 className="w-3.5 h-3.5" data-id="element-2396" />
              Most Violated Rules
            </h4>
            <div className="space-y-2" data-id="element-2397">
              {ruleBreakdown.map((item, idx) => <div key={idx} className="flex items-center gap-3" data-id="element-2398">
                  <div className="w-full" data-id="element-2399">
                    <div className="flex items-center justify-between mb-0.5" data-id="element-2400">
                      <span className="text-xs font-medium text-slate-700 truncate max-w-xs" data-id="element-2401">
                        {item.message}
                      </span>
                      <div className="flex items-center gap-2 shrink-0 ml-2" data-id="element-2402">
                        <Badge variant={item.severity === 'critical' ? 'danger' : item.severity === 'warning' ? 'warning' : 'info'} size="sm" data-id="element-2403">
                          {item.severity}
                        </Badge>
                        <span className="text-xs font-bold text-slate-500 w-8 text-right" data-id="element-2404">
                          ×{item.count}
                        </span>
                      </div>
                    </div>
                    <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-2405">
                      <div className={`h-full rounded-full ${item.severity === 'critical' ? 'bg-rose-400' : item.severity === 'warning' ? 'bg-amber-400' : 'bg-blue-400'}`} style={{
                  width: `${Math.min(100, item.count / (ruleBreakdown[0]?.count || 1) * 100)}%`
                }} data-id="element-2406" />
                    </div>
                  </div>
                </div>)}
            </div>
          </div>}
      </Card>

      {rows.length === 0 ? <Card className="p-12 text-center" data-id="element-2407">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" data-id="element-2408" />
          <h3 className="text-lg font-bold text-slate-900" data-id="element-2409">
            No governance issues
          </h3>
          <p className="text-slate-500" data-id="element-2410">
            All questions pass governance checks in the current filter scope.
          </p>
        </Card> : <div className="space-y-3" data-id="element-2411">
          {rows.slice(0, 100).map(row => <IssueCard key={row.questionId} questionId={row.questionId} module={row.module} tier={row.tier} group={row.group} issues={row.issues} data-id="element-2412" />)}
          {rows.length > 100 && <p className="text-center text-sm text-slate-500 py-2" data-id="element-2413">
              Showing 100 of {rows.length} — use filters to narrow results.
            </p>}
        </div>}
    </div>;
}
function IssueCard({
  questionId,
  module,
  tier,
  group,
  issues
}: {
  questionId: string;
  module: string;
  tier: number;
  group: number;
  issues: Array<{
    id: string;
    severity: string;
    message: string;
  }>;
}) {
  const hasCritical = issues.some(i => i.severity === 'critical');
  return <Card className={`p-4 border-l-4 ${hasCritical ? 'border-l-rose-500' : 'border-l-amber-400'}`} data-id="element-2414">
      <div className="flex items-center justify-between mb-3" data-id="element-2415">
        <code className="text-sm font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded" data-id="element-2416">
          {questionId}
        </code>
        <div className="flex gap-1.5" data-id="element-2417">
          <Badge variant="neutral" size="sm" data-id="element-2418">
            {module}
          </Badge>
          <Badge variant="neutral" size="sm" data-id="element-2419">
            T{tier} · G{group}
          </Badge>
        </div>
      </div>
      <ul className="space-y-1.5" data-id="element-2420">
        {issues.map(issue => <li key={issue.id} className="flex items-start gap-2 text-sm" data-id="element-2421">
            <SeverityIcon severity={issue.severity} data-id="element-2422" />
            <span className={issue.severity === 'critical' ? 'text-rose-700' : issue.severity === 'warning' ? 'text-amber-700' : 'text-slate-600'} data-id="element-2423">
              {issue.message}
            </span>
          </li>)}
      </ul>
    </Card>;
}
function SeverityIcon({
  severity
}: {
  severity: string;
}) {
  if (severity === 'critical') return <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" data-id="element-2424" />;
  if (severity === 'warning') return <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" data-id="element-2425" />;
  return <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" data-id="element-2426" />;
}